import { memo, useEffect, useRef, Component, useMemo } from "react";
import ChatbotAccordion from "./ChatbotAccordion";
import ChatbotSidePanelLoading from "./ChatbotSidePanelLoading";
import ChatbotOverview from "./ChatbotOverview";
import ChatbotWeddingDateProgress from "./ChatbotWeddingDateProgress";
import PrintButton from "../../../../../Assets/Icons/print-button.svg";
import { useReactToPrint } from "react-to-print";
import useGetWeddingSummary from "../../../../../Hooks/useGetWeddingSummary";
import { PanelDataList } from "../constant";
import GuestList from "./GuestList";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error in ChatbotSidePanel:", error, errorInfo);
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-message">
          Something went wrong. Please try again.
        </div>
      );
    }

    return this.props.children;
  }
}

const ChatbotSidePanel = memo(
  ({
    chatId,
    panelData,
    initialParsing,
    onErroDataParsing,
    isParsing,
    disableAction,
  }) => {
    const { key_info, extra_recommendations, ...rest } = panelData || {};

    const sideRef = useRef(null);

    const handlePrint = useReactToPrint({ contentRef: sideRef });

    const { data: summaryData, getData: getDataWeddingSummary } =
      useGetWeddingSummary({ chatId });

    const onPrint = () => {
      handlePrint();
    };

    const parsedSummaryData = useMemo(() => {
      if (summaryData) {
        const parsed = {};
        Object.entries(summaryData).forEach(([key, value]) => {
          const parsedKey = key?.replace("summary_", "");
          if (parsedKey.includes("timeline_")) {
            const timelineKey = parsedKey.replace("timeline_", "");
            if (parsed["timeline"]) {
              parsed["timeline"] = {
                ...parsed["timeline"],
                [timelineKey]: value,
              };
            } else {
              parsed["timeline"] = {
                [timelineKey]: value,
              };
            }
          } else if (!PanelDataList.includes(parsedKey)) {
            if (parsed["extra_recommendations"]) {
              parsed["extra_recommendations"] = {
                ...parsed["extra_recommendations"],
                [parsedKey]: value,
              };
            } else {
              parsed["extra_recommendations"] = {
                [parsedKey]: value,
              };
            }
          } else {
            parsed[parsedKey] = value;
          }
        });

        return parsed;
      }
      return {};
    }, [summaryData]);

    useEffect(() => {
      if (sideRef.current) {
        setTimeout(() => {
          sideRef.current?.classList.add("slide-in");
        }, 10);
      }
    }, []);

    useEffect(() => {
      window.addEventListener("mutateSidePanel", () => {
        getDataWeddingSummary();
      });
      window.addEventListener("updateTimeline", () => {
        getDataWeddingSummary();
      });

      return () => {
        window.removeEventListener("mutateSidePanel", () => {
          getDataWeddingSummary();
        });
        window.removeEventListener("updateTimeline", () => {
          getDataWeddingSummary();
        });
      };
    }, [getDataWeddingSummary]);

    return (
      <ErrorBoundary onError={onErroDataParsing}>
        <div
          className="chatbot-panel chatbot-panel__side transition-all"
          ref={sideRef}
        >
          <div className="print-capsule-container">
            <img
              src={PrintButton}
              alt="print"
              className="print-button"
              onClick={onPrint}
            />
          </div>
          <div className="guest-list-container">
            <GuestList chatId={chatId} />
          </div>
          <div className="content">
            {initialParsing ? (
              <ChatbotSidePanelLoading />
            ) : (
              <>
                <ChatbotOverview key_info={key_info} />
                <ChatbotWeddingDateProgress
                  weddingDate={key_info?.wedding_date}
                  createdWedingTimelineDate={key_info?.starting_date}
                />
                {Object.entries(rest).map(
                  ([key, value]) =>
                    value && (
                      <ChatbotAccordion
                        key={key}
                        chatId={chatId}
                        panel={{ ...value, type: key }}
                        isParsing={isParsing}
                        disableAction={disableAction}
                        parsedSummaryData={parsedSummaryData}
                      />
                    )
                )}
                {extra_recommendations?.map((recommendation, idx) => (
                  <ChatbotAccordion
                    key={idx}
                    isExtraRecommendation
                    panel={recommendation}
                    parsedSummaryData={parsedSummaryData}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </ErrorBoundary>
    );
  }
);

export default ChatbotSidePanel;
